import React from "react";
import { Box, Button, Card, Stack, Text, Alert, IconPrint } from "braid-design-system";
import Loading from "../components/Loading";
import { useAuth } from "../contexts/AuthProvider";
import { useToast } from "../hooks/useToast";
import {
    createCashReceipt,
    getAllCashReceipts,
    getCashReceiptContext,
    CashReceiptContext,
    CashReceiptMode,
    CashReceiptResponse,
} from "../services/cashReceiptService";
import { getSystemSetup, SetupResponse } from "../services/setupService";
import { sanitizeNumericString, isValidNumericString } from "../utils/numericInput";
import { getStationId } from "../utils/terminalStation";
import {
    CASH_MODE_LABELS,
    CashReceiptPrintHeader,
    formatMoney,
    formatServerDate,
    formatServerTime,
    printCashReceiptReport,
    printCashReceiptSlip,
} from "./cashReceipt/cashReceiptPrint";

// pos_cash_rec_with.payment_description
const DESCRIPTION_MAX_LENGTH = 50;
// One counter's entries for a day; the list is not paged
const LIST_LIMIT = 500;

const ALL_SHIFTS = "all";

// Used until the server date arrives with the open shift (or when not signed on)
const getBrowserDate = () => {
    const now = new Date();
    const yyyy = now.getFullYear();
    const mm = String(now.getMonth() + 1).padStart(2, "0");
    const dd = String(now.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

const formatLocationLabel = (code?: string | null, description?: string | null) =>
    code ? `${code}${description ? ` - ${description}` : ""}` : "-";

const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "10px",
    borderRadius: "6px",
    border: "1px solid #dfe3e8",
    marginTop: "6px",
};

const headerCellStyle: React.CSSProperties = { textAlign: "left", padding: "10px", borderBottom: "1px solid #dfe3e8" };
const cellStyle: React.CSSProperties = { padding: "10px", borderBottom: "1px solid #f0f4f8" };

const InfoField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <Box style={{ flex: 1 }}>
        <Text tone="secondary">{label}</Text>
        <Box background="surface" borderRadius="standard" padding="small" style={{ marginTop: "6px", textAlign: "left" }}>
            <Text>{value}</Text>
        </Box>
    </Box>
);

const CashReceipt: React.FC = () => {
    const { user } = useAuth();
    const toast = useToast();
    const [paymentMode, setPaymentMode] = React.useState<CashReceiptMode>("R");
    const [amount, setAmount] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [shiftContext, setShiftContext] = React.useState<CashReceiptContext | null>(null);
    const [contextLoaded, setContextLoaded] = React.useState(false);
    const [companySetup, setCompanySetup] = React.useState<SetupResponse | null>(null);
    const [cashReceiptList, setCashReceiptList] = React.useState<CashReceiptResponse[]>([]);
    const [filterDate, setFilterDate] = React.useState(getBrowserDate);
    const [filterShift, setFilterShift] = React.useState<string>(ALL_SHIFTS);
    const [loading, setLoading] = React.useState(false);
    const [saving, setSaving] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [formError, setFormError] = React.useState<string | null>(null);

    const terminalStation = (getStationId() || "").trim();
    const userName = user?.user_name ?? "-";
    const activeBranch = user?.active_branch;
    // The list follows this terminal's counter at the shift's location (or the user's branch when signed off)
    const listLocation = shiftContext?.station_loc || user?.active_branch_id || "";
    const currentLocationLabel = shiftContext
        ? formatLocationLabel(shiftContext.station_loc, shiftContext.loc_desc)
        : formatLocationLabel(activeBranch?.loc_code ?? user?.active_branch_id, activeBranch?.loc_desc);

    const signedOnElsewhere = Boolean(
        shiftContext && terminalStation && shiftContext.station_id.toUpperCase() !== terminalStation.toUpperCase()
    );
    const canRecord = Boolean(shiftContext && terminalStation && !signedOnElsewhere);

    const loadShiftContext = React.useCallback(async () => {
        try {
            const data = await getCashReceiptContext();
            setShiftContext(data);
            return data;
        } catch (contextError: any) {
            setShiftContext(null);
            setError(contextError?.message || "Unable to check your open shift.");
            return null;
        }
    }, []);

    React.useEffect(() => {
        let isMounted = true;
        void (async () => {
            const data = await loadShiftContext();
            if (!isMounted) return;
            if (data?.server_date) {
                setFilterDate(data.server_date);
            }
            setContextLoaded(true);
        })();
        // Company name/address are only for the printouts
        getSystemSetup()
            .then((setup) => isMounted && setCompanySetup(setup))
            .catch(() => undefined);
        return () => {
            isMounted = false;
        };
    }, [loadShiftContext]);

    const fetchData = React.useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getAllCashReceipts({
                payment_loc: listLocation || undefined,
                payment_date: filterDate,
                payment_station: terminalStation || undefined,
                limit: LIST_LIMIT,
            });
            setCashReceiptList(data);
        } catch (fetchError: any) {
            const message = fetchError?.message || "Unable to load cash receipt data.";
            setError(message);
            toast.showError(message);
        } finally {
            setLoading(false);
        }
    }, [toast, listLocation, filterDate, terminalStation]);

    React.useEffect(() => {
        if (contextLoaded) {
            fetchData();
        }
    }, [contextLoaded, fetchData]);

    const shiftOptions = React.useMemo(
        () =>
            Array.from(new Set(cashReceiptList.map((receipt) => receipt.payment_shift).filter((shift): shift is number => shift != null)))
                .sort((a, b) => a - b),
        [cashReceiptList]
    );

    const visibleReceipts = React.useMemo(
        () =>
            filterShift === ALL_SHIFTS
                ? cashReceiptList
                : cashReceiptList.filter((receipt) => String(receipt.payment_shift) === filterShift),
        [cashReceiptList, filterShift]
    );

    const totals = React.useMemo(() => {
        const sum = (mode: string) =>
            visibleReceipts
                .filter((receipt) => receipt.payment_mode === mode)
                .reduce((total, receipt) => total + Number(receipt.payment_amount || 0), 0);
        const receipts = sum("R");
        const withdrawals = sum("W");
        return { receipts, withdrawals, net: receipts - withdrawals };
    }, [visibleReceipts]);

    const printHeaderFor = (locationCode?: string | null): CashReceiptPrintHeader => ({
        companyName: companySetup?.setup_comname || "",
        companyAddress: companySetup?.setup_comaddress || "",
        companyTele: companySetup?.setup_comtele || "",
        locationLabel: !locationCode || locationCode === listLocation ? currentLocationLabel : locationCode,
    });

    const handlePrintError = (message: string) => toast.showError(message);

    const handleReprint = (receipt: CashReceiptResponse) =>
        printCashReceiptSlip(receipt, printHeaderFor(receipt.payment_loc), { isReprint: true, onError: handlePrintError });

    const handlePrintReport = () => {
        if (loading) return;
        printCashReceiptReport(
            visibleReceipts,
            {
                ...printHeaderFor(listLocation),
                date: filterDate,
                counter: terminalStation || "All",
                shiftLabel: filterShift === ALL_SHIFTS ? "All" : filterShift,
                printedBy: userName,
            },
            handlePrintError
        );
    };

    const validateForm = () => {
        const trimmedAmount = amount.trim();
        if (!trimmedAmount) {
            setFormError("Amount is required.");
            return false;
        }

        if (!isValidNumericString(trimmedAmount) || !(Number(trimmedAmount) > 0)) {
            setFormError("Amount must be a number greater than 0.");
            return false;
        }

        if (description.trim().length > DESCRIPTION_MAX_LENGTH) {
            setFormError(`Description can be at most ${DESCRIPTION_MAX_LENGTH} characters.`);
            return false;
        }

        setFormError(null);
        return true;
    };

    const handleSubmit = async () => {
        if (!canRecord || !validateForm()) {
            return;
        }

        try {
            setSaving(true);
            setError(null);
            const saved = await createCashReceipt({
                payment_mode: paymentMode,
                payment_amount: Number(amount),
                payment_description: description.trim() || undefined,
            });

            toast.showSuccess(`${CASH_MODE_LABELS[paymentMode]} saved`, `Payment No: ${saved.payment_no}`);
            setPaymentMode("R");
            setAmount("");
            setDescription("");
            printCashReceiptSlip(saved, printHeaderFor(saved.payment_loc), { onError: handlePrintError });

            // Show the day the entry was saved on
            const savedDate = (saved.payment_date || "").slice(0, 10);
            setFilterShift(ALL_SHIFTS);
            if (savedDate && savedDate !== filterDate) {
                setFilterDate(savedDate);
            } else {
                await fetchData();
            }
        } catch (submitError: any) {
            const message = submitError?.message || "Failed to save cash receipt.";
            setError(message);
            toast.showError(message);
            // The shift may have been closed elsewhere
            await loadShiftContext();
        } finally {
            setSaving(false);
        }
    };

    const recordBlockedMessage = !contextLoaded
        ? null
        : !shiftContext
            ? "You are not signed on to a counter. Sign on to record cash receipts or withdrawals."
            : !terminalStation
                ? "This terminal has no counter assigned. Set it up in Terminal Setup first."
                : signedOnElsewhere
                    ? `You are signed on to Counter ${shiftContext.station_id}. Cash receipts and withdrawals can only be recorded on that counter, not Counter ${terminalStation}.`
                    : null;

    return (
        <Box padding="medium">
            <Card>
                <Box padding="medium">
                    <Stack space="large">
                        <Text weight="strong" size="large">
                            Cash Receipt
                        </Text>

                        {error ? (
                            <Alert tone="critical">
                                <Text>
                                    <span>{error}</span>
                                </Text>
                            </Alert>
                        ) : null}

                        <Card>
                            <Box padding="medium">
                                <Stack space="medium">
                                    {recordBlockedMessage ? (
                                        <Alert tone="caution">
                                            <Text>{recordBlockedMessage}</Text>
                                        </Alert>
                                    ) : null}

                                    {/* First Row: Type and Amount */}
                                    <Box display="flex" flexDirection="row" gap="small">
                                        <Box style={{ flex: 1 }}>
                                            <Text tone="secondary">Type</Text>
                                            <select
                                                value={paymentMode}
                                                onChange={(e) => setPaymentMode(e.target.value as CashReceiptMode)}
                                                disabled={!canRecord}
                                                style={inputStyle}
                                            >
                                                <option value="R">{CASH_MODE_LABELS.R}</option>
                                                <option value="W">{CASH_MODE_LABELS.W}</option>
                                            </select>
                                        </Box>

                                        <Box style={{ flex: 1 }}>
                                            <Text tone="secondary">Amount</Text>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                value={amount}
                                                onChange={(e) => {
                                                    setAmount(sanitizeNumericString(e.target.value));
                                                }}
                                                disabled={!canRecord}
                                                style={inputStyle}
                                                placeholder="Enter amount"
                                            />
                                        </Box>
                                    </Box>

                                    {/* Taken from the open shift by the server, not editable */}
                                    <Box display="flex" flexDirection="row" gap="small">
                                        <InfoField label="Location" value={currentLocationLabel} />
                                        <InfoField label="Date" value={shiftContext ? formatServerDate(shiftContext.server_date) : "-"} />
                                    </Box>

                                    <Box display="flex" flexDirection="row" gap="small">
                                        <InfoField label="Cashier" value={shiftContext?.user_name ?? userName} />
                                        <InfoField label="Counter" value={shiftContext?.station_id ?? (terminalStation || "-")} />
                                        <InfoField label="Shift" value={shiftContext?.shift_no ?? "-"} />
                                    </Box>

                                    {/* Description Field */}
                                    <Box>
                                        <Box display="flex" justifyContent="spaceBetween">
                                            <Text tone="secondary">Description</Text>
                                            <Text tone="secondary" size="small">
                                                {description.length}/{DESCRIPTION_MAX_LENGTH}
                                            </Text>
                                        </Box>
                                        <textarea
                                            value={description}
                                            onChange={(e) => setDescription(e.target.value)}
                                            maxLength={DESCRIPTION_MAX_LENGTH}
                                            disabled={!canRecord}
                                            style={{ ...inputStyle, minHeight: "80px", resize: "vertical" }}
                                            placeholder="Enter description"
                                        />
                                    </Box>

                                    {formError ? (
                                        <Alert tone="critical">
                                            <Text>
                                                <span>{formError}</span>
                                            </Text>
                                        </Alert>
                                    ) : null}

                                    {/* Braid buttons can't be disabled; the caution above says why it's missing */}
                                    {canRecord ? (
                                        <Button
                                            variant="solid"
                                            tone="brandAccent"
                                            onClick={handleSubmit}
                                            loading={saving}
                                        >
                                            {`Save ${CASH_MODE_LABELS[paymentMode]}`}
                                        </Button>
                                    ) : null}
                                </Stack>
                            </Box>
                        </Card>

                        <Card>
                            <Box padding="medium">
                                <Stack space="medium">
                                    <Box display="flex" flexDirection="row" gap="small" alignItems="flexEnd" flexWrap="wrap">
                                        <Box style={{ flex: 2, minWidth: "180px" }}>
                                            <Text weight="strong">Cash Receipts &amp; Withdrawals</Text>
                                            <Box paddingTop="xsmall">
                                                <Text tone="secondary" size="small">
                                                    {`Counter ${terminalStation || "All"} · ${currentLocationLabel}`}
                                                </Text>
                                            </Box>
                                        </Box>
                                        <Box style={{ flex: 1, minWidth: "150px" }}>
                                            <Text tone="secondary">Date</Text>
                                            <input
                                                type="date"
                                                value={filterDate}
                                                onChange={(e) => {
                                                    if (e.target.value) {
                                                        setFilterDate(e.target.value);
                                                        setFilterShift(ALL_SHIFTS);
                                                    }
                                                }}
                                                style={inputStyle}
                                            />
                                        </Box>
                                        <Box style={{ flex: 1, minWidth: "120px" }}>
                                            <Text tone="secondary">Shift</Text>
                                            <select
                                                value={filterShift}
                                                onChange={(e) => setFilterShift(e.target.value)}
                                                style={inputStyle}
                                            >
                                                <option value={ALL_SHIFTS}>All shifts</option>
                                                {shiftOptions.map((shift) => (
                                                    <option key={shift} value={String(shift)}>{`Shift ${shift}`}</option>
                                                ))}
                                            </select>
                                        </Box>
                                        <Box>
                                            <Button
                                                variant="ghost"
                                                tone="brandAccent"
                                                icon={<IconPrint />}
                                                onClick={handlePrintReport}
                                            >
                                                Print Report
                                            </Button>
                                        </Box>
                                    </Box>

                                    {loading ? (
                                        <Loading message="Loading receipts..." />
                                    ) : visibleReceipts.length === 0 ? (
                                        <Text tone="secondary">No records found.</Text>
                                    ) : (
                                        <Box style={{ overflowX: "auto" }}>
                                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                                                <thead>
                                                    <tr>
                                                        <th style={headerCellStyle}>Payment No</th>
                                                        <th style={headerCellStyle}>Time</th>
                                                        <th style={headerCellStyle}>Type</th>
                                                        <th style={headerCellStyle}>Shift</th>
                                                        <th style={headerCellStyle}>Cashier</th>
                                                        <th style={headerCellStyle}>Description</th>
                                                        <th style={{ ...headerCellStyle, textAlign: "right" }}>Amount</th>
                                                        <th style={headerCellStyle} aria-label="Actions" />
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {visibleReceipts.map((receipt) => (
                                                        <tr key={receipt.payment_no}>
                                                            <td style={cellStyle}>{receipt.payment_no}</td>
                                                            <td style={cellStyle}>{formatServerTime(receipt.payment_time)}</td>
                                                            <td style={cellStyle}>{CASH_MODE_LABELS[receipt.payment_mode ?? ""] ?? receipt.payment_mode ?? "-"}</td>
                                                            <td style={cellStyle}>{receipt.payment_shift ?? "-"}</td>
                                                            <td style={cellStyle}>{receipt.payment_cashier_id ?? "-"}</td>
                                                            <td style={cellStyle}>{receipt.payment_description ?? "-"}</td>
                                                            <td style={{ ...cellStyle, textAlign: "right" }}>
                                                                {receipt.payment_mode === "W" ? "-" : ""}
                                                                {formatMoney(receipt.payment_amount)}
                                                            </td>
                                                            <td style={{ ...cellStyle, textAlign: "right" }}>
                                                                <Button size="small" variant="transparent" onClick={() => handleReprint(receipt)}>
                                                                    Reprint
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                                <tfoot>
                                                    <tr>
                                                        <td colSpan={6} style={{ ...cellStyle, textAlign: "right" }}>
                                                            <Text tone="secondary">Receipts</Text>
                                                        </td>
                                                        <td style={{ ...cellStyle, textAlign: "right" }}>{formatMoney(totals.receipts)}</td>
                                                        <td style={cellStyle} />
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={6} style={{ ...cellStyle, textAlign: "right" }}>
                                                            <Text tone="secondary">Withdrawals</Text>
                                                        </td>
                                                        <td style={{ ...cellStyle, textAlign: "right" }}>-{formatMoney(totals.withdrawals)}</td>
                                                        <td style={cellStyle} />
                                                    </tr>
                                                    <tr>
                                                        <td colSpan={6} style={{ ...cellStyle, textAlign: "right" }}>
                                                            <Text weight="strong">Net</Text>
                                                        </td>
                                                        <td style={{ ...cellStyle, textAlign: "right", fontWeight: 700 }}>
                                                            {totals.net < 0 ? "-" : ""}
                                                            {formatMoney(Math.abs(totals.net))}
                                                        </td>
                                                        <td style={cellStyle} />
                                                    </tr>
                                                </tfoot>
                                            </table>
                                        </Box>
                                    )}
                                </Stack>
                            </Box>
                        </Card>
                    </Stack>
                </Box>
            </Card>
        </Box>
    );
};

export default CashReceipt;
